import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import useCanonicalUrl from "@/hooks/useCanonicalUrl";
import { StockailyWordmark } from "@/components/brand/StockailyBrand";
import { useConsentUi } from "@/components/consent/ConsentProvider";
import Footer from "@/components/Footer";

/**
 * WS-11 Final Launch Reconciliation — canonical legal-page shell.
 *
 * Every one of the 14 legal documents renders through this component
 * with:
 *   * consistent Stockaily header (clickable wordmark → homepage)
 *   * language switch (DE ⇄ EN) resolving to the sibling document
 *   * single H1 (no duplicated title above the document body)
 *   * responsive max-width + typography + spacing
 *   * accessible keyboard focus states and semantic headings
 *   * no pre-launch / owner-review / draft / placeholder banners
 *   * no rendering of internal machine-readable status flags
 */
const LANGUAGE_SIBLINGS = {
  "de_impressum": "en_legal_notice",
  "de_datenschutz": "en_privacy",
  "de_agb": "en_terms",
  "de_disclaimer": "en_disclaimer",
  "de_cookies": "en_cookies",
  "de_widerruf": "en_withdrawal",
  "de_widerrufsformular": "en_withdrawal_form",
  "en_legal_notice": "de_impressum",
  "en_privacy": "de_datenschutz",
  "en_terms": "de_agb",
  "en_disclaimer": "de_disclaimer",
  "en_cookies": "de_cookies",
  "en_withdrawal": "de_widerruf",
  "en_withdrawal_form": "de_widerrufsformular",
};

/**
 * Convert the machine-readable ISO effective-from timestamp (e.g.
 * `2026-08-01T00:00:00Z`) into a locale-aware human-readable date
 * (`01.08.2026` for DE, `August 1, 2026` for EN).  Formatting always
 * uses UTC so that a viewer in an earlier timezone does not see a
 * day-shifted value; the internal machine-readable source date is
 * preserved by the caller and is not overwritten by this helper.
 */
export function formatEffectiveDate(iso, language) {
  if (!iso || typeof iso !== "string") return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-US", {
      year: "numeric",
      month: language === "de" ? "2-digit" : "long",
      day: language === "de" ? "2-digit" : "numeric",
      timeZone: "UTC",
    }).format(d);
  } catch {
    return iso;
  }
}

const ROUTE_FOR = {
  "de_impressum": "/de/impressum",
  "de_datenschutz": "/de/datenschutz",
  "de_agb": "/de/agb",
  "de_disclaimer": "/de/disclaimer",
  "de_cookies": "/de/cookies",
  "de_widerruf": "/de/widerruf",
  "de_widerrufsformular": "/de/widerrufsformular",
  "en_legal_notice": "/en/legal-notice",
  "en_privacy": "/en/privacy",
  "en_terms": "/en/terms",
  "en_disclaimer": "/en/disclaimer",
  "en_cookies": "/en/cookies",
  "en_withdrawal": "/en/withdrawal",
  "en_withdrawal_form": "/en/withdrawal-form",
};

function LegalShell({ children, language, documentId, title, siblingRoute }) {
  return (
    <div
      className="min-h-screen bg-background text-foreground"
      lang={language}
      data-testid={`legal-shell-${documentId}`}
    >
      <header
        className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur"
        data-testid="legal-shell-header"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4 px-4 py-3">
          <Link
            to="/"
            aria-label="Stockaily home"
            className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 inline-flex items-center gap-2"
            data-testid="legal-shell-home-link"
          >
            <StockailyWordmark height={22} />
          </Link>
          <nav aria-label="Legal language switch" className="text-sm flex items-center gap-3">
            <Link
              to={siblingRoute || "/"}
              className="text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded px-1 py-0.5"
              data-testid="legal-shell-language-switch"
            >
              {language === "de" ? "English" : "Deutsch"}
            </Link>
          </nav>
        </div>
      </header>
      <main
        className="max-w-3xl mx-auto px-4 py-8 space-y-6"
        data-testid={`legal-page-${documentId}`}
      >
        <h1
          className="text-3xl font-semibold tracking-tight"
          data-testid="legal-page-title"
        >
          {title}
        </h1>
        {children}
      </main>
      {/* Slice B/C follow-up (2026-02-08): mount the shared app Footer
          on the language-scoped legal pages so the same footer
          cookie-settings entry point is reachable here too. Opening
          from the footer on /de/cookies or /en/cookies uses the same
          shared modal and inherits the route-based language override
          from `ConsentProvider.resolveRouteLanguage()`. */}
      <Footer />
    </div>
  );
}

export default function LegalPage({ documentId, language, title }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const loc = useLocation();
  const siblingRoute = useMemo(
    () => ROUTE_FOR[LANGUAGE_SIBLINGS[documentId]] || null,
    [documentId],
  );
  // WS-11 Correction Round — emit the canonical URL for THIS document.
  // The canonical route is derived from the deterministic ROUTE_FOR
  // map (never from loc.pathname), so a legacy `/legal/*` mount could
  // not accidentally become canonical even if this component were
  // reached through an unexpected route in the future.
  const canonicalPath = ROUTE_FOR[documentId] || null;
  useCanonicalUrl(canonicalPath);

  useEffect(() => {
    document.title = `${title || "Legal"} — Stockaily`;
    // WS-11 Correction Round v2 (2026-08-02): fetch the document via
    // the /api-prefixed companion endpoint.  The bare canonical paths
    // (`/de/agb`, `/en/privacy`, …) are also served by FastAPI, but
    // the Kubernetes ingress only forwards `/api/*` traffic to the
    // backend — a direct fetch to the bare path would hit the frontend
    // static server on a deployed environment and 404.  The companion
    // endpoint returns the same `_doc_public` payload.
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const url =
      `${backend}/api/legal/document?route=${encodeURIComponent(loc.pathname)}`;
    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setDoc)
      .catch((e) => setErr(String(e)));
  }, [loc.pathname, title]);

  if (err) {
    return (
      <LegalShell
        language={language}
        documentId={documentId}
        title={title}
        siblingRoute={siblingRoute}
      >
        <p className="text-sm text-muted-foreground">
          The legal document could not be loaded. Please try again later.
        </p>
      </LegalShell>
    );
  }
  if (!doc) {
    return (
      <LegalShell
        language={language}
        documentId={documentId}
        title={title}
        siblingRoute={siblingRoute}
      >
        <p className="text-sm text-muted-foreground" data-testid="legal-page-loading">
          Loading…
        </p>
      </LegalShell>
    );
  }

  return (
    <LegalShell
      language={doc.language}
      documentId={documentId}
      title={title}
      siblingRoute={siblingRoute}
    >
      <p className="text-xs text-muted-foreground" data-testid="legal-page-meta">
        <span data-testid="legal-page-version">Version: {doc.version}</span>
        {" · "}
        <span data-testid="legal-page-effective-from">
          {doc.language === "de" ? "Gültig ab: " : "Effective from: "}
          {formatEffectiveDate(doc.effective_from, doc.language)}
        </span>
        {" · "}
        <span data-testid="legal-page-language">
          {doc.language === "de" ? "Sprache: " : "Language: "}
          {(doc.language || "").toUpperCase()}
        </span>
      </p>

      <section data-testid="legal-page-operator">
        <h2 className="text-lg font-semibold">
          {doc.language === "de" ? "Anbieter" : "Operator"}
        </h2>
        <address className="not-italic text-sm mt-1 whitespace-pre-line">
          {doc.operator?.postal_address?.display_multi_line ||
            [
              doc.operator?.legal_name,
              doc.operator?.trading_name,
              doc.operator?.legal_form,
              doc.operator?.country,
            ]
              .filter(Boolean)
              .join(" · ")}
        </address>
        {/* Locale-aware secondary line so the English page shows an
            English rendering of the legal form and country, and the
            German page shows the German official designation. */}
        <p className="text-xs text-muted-foreground mt-1" data-testid="legal-page-operator-legal-form">
          {doc.operator?.legal_form}
        </p>
        <p className="text-sm mt-2">
          <a
            href={`mailto:${doc.operator?.support_email}`}
            className="underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
            data-testid="legal-page-operator-email"
          >
            {doc.operator?.support_email}
          </a>
        </p>
        {doc.operator?.public_phone && (
          <p className="text-sm mt-1" data-testid="legal-page-operator-phone">
            {doc.language === "de" ? "Telefon: " : "Phone: "}
            <a
              href={`tel:${doc.operator.public_phone.replace(/\s+/g, "")}`}
              className="underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
              data-testid="legal-page-operator-phone-link"
            >
              {doc.operator.public_phone}
            </a>
          </p>
        )}
        {doc.statutory_basis && (
          <p
            className="text-xs text-muted-foreground mt-2"
            data-testid="legal-page-statutory-basis"
          >
            {doc.language === "de" ? "Angaben gemäß " : "Information pursuant to "}
            {doc.statutory_basis}
          </p>
        )}
      </section>

      {/* WS-11 Correction Round v2 (2026-08-02) — substantive body
          sections for every document type.  Content comes from the
          centralized backend module `services/ws11_legal_bodies.py`
          so German and English stay structurally identical, and so
          the release gate can verify required section identifiers per
          document type.  Cookie policy still renders through its
          dedicated `cookie_policy_body` block further below. */}
      {Array.isArray(doc.body_sections) && doc.body_sections.length > 0 && (
        <section
          data-testid={`legal-page-body-${doc.document_type}`}
          className="space-y-6"
        >
          {doc.body_sections.map((sec, i) => (
            <div
              key={sec.id || i}
              data-testid={`legal-section-${sec.id}`}
              className="space-y-2"
            >
              <h2
                className="text-lg font-semibold"
                data-testid={`legal-section-${sec.id}-heading`}
              >
                {sec.heading}
              </h2>
              {(sec.paragraphs || []).map((p, pi) => (
                <p
                  key={pi}
                  className="text-sm leading-relaxed whitespace-pre-line"
                  data-testid={`legal-section-${sec.id}-p-${pi}`}
                >
                  {renderParagraphWithMailto(p)}
                </p>
              ))}
              {Array.isArray(sec.bullets) && sec.bullets.length > 0 && (
                <ul
                  className="list-disc pl-6 space-y-1 text-sm leading-relaxed"
                  data-testid={`legal-section-${sec.id}-bullets`}
                >
                  {sec.bullets.map((b, bi) => (
                    <li key={bi}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      )}

      {/* WS-11 Privacy Policy retention schedule — rendered only for
          privacy_policy documents. The row content comes from the
          backend registry so the registry and public copy cannot drift. */}
      {doc.document_type === "privacy_policy" &&
        Array.isArray(doc.retention_schedule) &&
        doc.retention_schedule.length > 0 && (
          <section data-testid="legal-page-retention-schedule" className="text-sm">
            <h2 className="text-lg font-semibold">
              {doc.language === "de"
                ? "Aufbewahrungsfristen"
                : "Data-retention schedule"}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {doc.language === "de"
                ? "Wir bewahren Daten nur so lange auf, wie es für den jeweils angegebenen Zweck erforderlich ist."
                : "We keep personal data only as long as required for the purpose stated below."}
            </p>
            <div className="mt-3 overflow-x-auto">
              <table
                className="w-full text-xs border-collapse"
                data-testid="legal-page-retention-table"
              >
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left py-1.5 pr-3 font-medium">
                      {doc.language === "de" ? "Kategorie" : "Category"}
                    </th>
                    <th className="text-left py-1.5 pr-3 font-medium">
                      {doc.language === "de" ? "Zweck" : "Purpose"}
                    </th>
                    <th className="text-left py-1.5 pr-3 font-medium">
                      {doc.language === "de" ? "Frist" : "Standard period"}
                    </th>
                    <th className="text-left py-1.5 pr-3 font-medium">
                      {doc.language === "de" ? "Maximal" : "Maximum"}
                    </th>
                    <th className="text-left py-1.5 pr-3 font-medium">
                      {doc.language === "de" ? "Auslöser" : "Trigger"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {doc.retention_schedule.map((row) => (
                    <tr
                      key={row.category_id}
                      className="border-b border-border/30 align-top"
                      data-testid={`retention-row-${row.category_id}`}
                    >
                      <td className="py-1.5 pr-3 font-medium">{row.category}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">
                        {row.purpose}
                      </td>
                      <td className="py-1.5 pr-3">{row.standard_period}</td>
                      <td className="py-1.5 pr-3">{row.maximum_period}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">
                        {row.trigger}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

      {/* Consent Foundation (2026-08-02) — cookie policy body.
          Rendered only for cookie_policy documents so the /de/cookies
          and /en/cookies pages describe the exact runtime consent
          surface (categories, storage mechanism, controls).  The body
          comes from the backend `OPERATOR_FACTS.cookie_policy_body`
          so the policy text and the runtime banner cannot drift. */}
      {doc.document_type === "cookie_policy" && doc.cookie_policy_body && (
        <section data-testid="legal-page-cookie-policy-body" className="space-y-5">
          <p className="text-sm leading-relaxed" data-testid="cookie-policy-intro">
            {doc.cookie_policy_body.intro}
          </p>
          <div>
            <h2 className="text-lg font-semibold" data-testid="cookie-policy-categories-heading">
              {doc.cookie_policy_body.categories_heading}
            </h2>
            <ul className="mt-2 space-y-3" data-testid="cookie-policy-categories">
              {(doc.cookie_policy_body.categories || []).map((cat) => (
                <li
                  key={cat.key}
                  className="rounded-md border border-border/60 px-3 py-2"
                  data-testid={`cookie-policy-category-${cat.key}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{cat.name}</span>
                    {cat.always_active && (
                      <span
                        className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                        data-testid={`cookie-policy-category-${cat.key}-always-active`}
                      >
                        {doc.language === "de" ? "Immer aktiv" : "Always active"}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {cat.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-lg font-semibold" data-testid="cookie-policy-controls-heading">
              {doc.cookie_policy_body.controls_heading}
            </h2>
            <CookiePolicyControlsParagraph
              language={doc.language}
              text={doc.cookie_policy_body.controls}
            />
          </div>
          <div>
            <h2 className="text-lg font-semibold" data-testid="cookie-policy-storage-heading">
              {doc.cookie_policy_body.storage_heading}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground" data-testid="cookie-policy-storage">
              {doc.cookie_policy_body.storage}
            </p>
            {doc.consent_public_surface && (
              <dl
                className="mt-3 grid grid-cols-1 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2"
                data-testid="cookie-policy-consent-surface"
              >
                <dt className="font-medium text-foreground">
                  {doc.language === "de" ? "Speicherort" : "Storage"}
                </dt>
                <dd data-testid="cookie-policy-storage-mechanism">
                  {doc.consent_public_surface.storage_mechanism}
                </dd>
                <dt className="font-medium text-foreground">
                  {doc.language === "de" ? "Schlüssel" : "Key"}
                </dt>
                <dd data-testid="cookie-policy-storage-key">
                  {doc.consent_public_surface.storage_key}
                </dd>
                <dt className="font-medium text-foreground">
                  {doc.language === "de" ? "Schema-Version" : "Schema version"}
                </dt>
                <dd data-testid="cookie-policy-schema-version">
                  {doc.consent_public_surface.schema_version}
                </dd>
              </dl>
            )}
          </div>
        </section>
      )}

      {doc.language_authority_note && (
        <section
          data-testid="legal-page-language-authority"
          className="text-xs text-muted-foreground"
        >
          {doc.language_authority_note}
        </section>
      )}
    </LegalShell>
  );
}

/**
 * Slice B/C follow-up (2026-02-08) — cookie-policy controls paragraph.
 *
 * The WS-11 `cookie_policy_body.controls` text already contains the
 * phrase "Cookie settings" / „Cookie-Einstellungen" (surrounded by
 * curly quotes) and directs the reader to the site footer. This
 * helper preserves the canonical legal text verbatim and only
 * substitutes that quoted phrase with a real, keyboard-accessible
 * <button> that opens the SAME granular settings dialog the footer
 * opens via `useConsentUi().openSettings()` — no second consent
 * implementation, no change to consent categories, defaults or
 * PostHog behavior.
 *
 * Fallback: if the expected quoted phrase is absent (older doc
 * revision), the paragraph renders unchanged, and no interactive
 * control is added — canonical text is never mutated.
 */
/**
 * Post-launch footer/contact update (2026-02-09) — render an email
 * address inside a legal-page paragraph as a functional mailto link
 * while preserving the surrounding text verbatim.  The paragraph's
 * textContent is unchanged, so existing tests that pin the string
 * remain valid.  We deliberately match only well-formed simple
 * addresses to avoid mis-linking arbitrary text.
 */
function renderParagraphWithMailto(text) {
  if (typeof text !== "string" || !text) return text;
  const emailRe = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
  const parts = text.split(emailRe);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <a
        key={i}
        href={`mailto:${part}`}
        className="underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
        data-testid={`legal-section-mailto-${part}`}
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}


function CookiePolicyControlsParagraph({ language, text }) {
  const { openSettings } = useConsentUi();
  // Open the SAME shared dialog but force it into the CURRENT ROUTE
  // LANGUAGE (`doc.language`) so a /de/cookies visit never shows an
  // English modal and vice versa. This is the explicit-language
  // override the ConsentProvider expects — no stale localStorage or
  // navigator-language can win against an explicit /de/ or /en/
  // route.
  const label = language === "de" ? "Cookie-Einstellungen" : "Cookie settings";
  // Match both curly and straight quote variants so the split works
  // regardless of which quoting style the backend copy uses.
  const patterns = language === "de"
    ? [`\u201eCookie-Einstellungen\u201c`, `"Cookie-Einstellungen"`]
    : [`\u201cCookie settings\u201d`, `"Cookie settings"`];
  let before = null;
  let after = null;
  for (const p of patterns) {
    const idx = text.indexOf(p);
    if (idx !== -1) {
      before = text.slice(0, idx);
      after = text.slice(idx + p.length);
      break;
    }
  }
  if (before == null) {
    // Canonical text did not contain the expected phrase — render
    // unchanged so we never mutate legal copy silently.
    return (
      <p
        className="mt-2 text-sm leading-relaxed text-muted-foreground"
        data-testid="cookie-policy-controls"
      >
        {text}
      </p>
    );
  }
  return (
    <p
      className="mt-2 text-sm leading-relaxed text-muted-foreground"
      data-testid="cookie-policy-controls"
    >
      {before}
      <button
        type="button"
        onClick={() => openSettings(language)}
        className="inline underline underline-offset-2 decoration-primary/60 hover:decoration-primary text-primary font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-sm"
        aria-label={label}
        data-testid="cookie-policy-open-settings"
      >
        {label}
      </button>
      {after}
    </p>
  );
}

